import { defineStore } from 'pinia'
import { ref } from 'vue'

import type { UserMeResponse } from '@/backend/types'
import { updateUser } from '@/backend/updateUser'
import { assertIsNotNullOrUndefined } from '@/shared/utils/typeAssertions'
import { useStorage } from '@vueuse/core'

export type User = {
  id: UserMeResponse['id']
  firstName: UserMeResponse['first_name']
  lastName: UserMeResponse['last_name']
  email: UserMeResponse['email']
  createdAt: Date
  acceptedTermsAndConditions: UserMeResponse['accepted_terms_and_conditions']
  marketingOptin: UserMeResponse['marketing_optin']
  signupCompleted: UserMeResponse['signup_completed']
  showTutorials: UserMeResponse['show_tutorials']
  profilePictureUrl: UserMeResponse['profile_picture_url']
}

export type UserRequest = {
  firstName: UserMeResponse['first_name']
  lastName: UserMeResponse['last_name']
  email: UserMeResponse['email']
  acceptedTermsAndConditions: UserMeResponse['accepted_terms_and_conditions']
  marketingOptin: UserMeResponse['marketing_optin']
  signupCompleted: UserMeResponse['signup_completed']
  showTutorials: UserMeResponse['show_tutorials']
  profilePictureUrl: UserMeResponse['profile_picture_url']
}

export const serializeUser = (response: UserMeResponse): User => ({
  id: response.id,
  firstName: response.first_name,
  lastName: response.last_name,
  email: response.email,
  createdAt: new Date(response.created_at),
  acceptedTermsAndConditions: response.accepted_terms_and_conditions,
  marketingOptin: response.marketing_optin,
  signupCompleted: response.signup_completed,
  showTutorials: response.show_tutorials,
  profilePictureUrl: response.profile_picture_url,
})

export const deserializeUser = (user: UserRequest): Omit<UserMeResponse, 'id' | 'created_at'> => ({
  first_name: user.firstName,
  last_name: user.lastName,
  email: user.email,
  accepted_terms_and_conditions: user.acceptedTermsAndConditions,
  marketing_optin: user.marketingOptin,
  signup_completed: user.signupCompleted,
  show_tutorials: user.showTutorials,
  profile_picture_url: user.profilePictureUrl,
})

type InvitationBackup = {
  token: string
  workspace_id: string
  project_id?: string
}

export const useUser = defineStore('user', () => {
  const user = ref<User | null>(null)
  const invitationStatus = ref<'pending' | 'error' | 'valid' | 'idle'>('idle')
  const invitationErrorMessage = ref<string>('')

  // Holds the invitation data from most recent invite acceptance atempt
  // Gets cleared when the network request to accept the attempt finishes, succesfully or not
  // Used to continue the acceptance process if it gets interrupted by things such as
  // - crashes
  // - network issues
  // - user manually refreshing the page
  // - email verification flow
  // - tfa setup flow
  const invitationBackup = useStorage<InvitationBackup | null>(
    'go:invitation',
    null,
    localStorage,
    // because the default is null, useStorage will default to "toString" as serializer
    // so we need to tell it to use JSON.stringify and JSON.parse
    {
      serializer: {
        read: (v) => (v ? JSON.parse(v) : null),
        write: (v) => JSON.stringify(v),
      },
    },
  )

  /**  The agent ID to which the user was invited (if any). */
  const invitationProjectId = useStorage<string | null>('invitation-project-id', null)

  const setUser = (newUser: User) => {
    user.value = newUser
  }

  const acceptUserInvite = async (workspaceId: string, inviteToken: string) => {
    invitationStatus.value = 'pending'

    if (!workspaceId || !inviteToken) {
      invitationStatus.value = 'error'
      invitationErrorMessage.value = 'Invalid invitation URL.'
      return
    }

    // This avoids a playwright error with imports.
    const { acceptInvitation } = await import('@/backend/acceptInvitation')

    const response = await acceptInvitation({ inviteToken, workspaceId })
    // remove the backup, the request has finished
    // it either succeeded in which case the user got what they came for
    // or it failed in which case the invite was bad and the user needs a new one
    invitationBackup.value = null

    if (!response.ok) {
      invitationStatus.value = 'error'
      invitationErrorMessage.value = response.error.message
      return
    }

    invitationStatus.value = 'valid'
  }

  const update = async (props: Parameters<typeof updateUser>[0]) => {
    assertIsNotNullOrUndefined(user.value)
    const oldUser = { ...user.value }

    // Assume that the update will go fine, propagate optimistic changes
    setUser({ ...oldUser, ...props })

    try {
      const response = await updateUser(props)
      if (!response.ok) {
        // updateUser can both throw and return an error, so handle them both by throwing
        throw new Error(response.error.message)
      }

      setUser(serializeUser(response.data))
    } catch (err) {
      setUser(oldUser)
      throw err
    }
  }

  return {
    user,
    setUser,
    update,
    acceptUserInvite,
    invitationBackup,
    invitationStatus,
    invitationErrorMessage,
    invitationProjectId,
  }
})
